/**
 * Filter questions by condition and repeat-form rules.
 *
 * Rules:
 * - First run (not returning): show ALL questions (subject to condition)
 * - Repeat run (returning): hide ONLY questions explicitly marked repeatable:false
 * - `required` affects validation only, never visibility
 */
export const filterQuestions = (allQs, formData, user, options = {}) => {
  const isRepeat = !!user?.returning;
  const visibilityData = options.visibilityData ?? formData;
  const hasAnswer = (value) =>
    value !== undefined &&
    value !== null &&
    !(typeof value === "string" && value.trim() === "");

  return allQs.filter((q) => {
    // condition check
    const cond =
      typeof q.condition === "function"
        ? q.condition.length === 1
          ? q.condition(formData)
          : q.condition(formData, user)
        : true;

    if (!cond) return false;

    // First-time user: show everything
    if (!isRepeat) return true;

    // Returning user: hide one-time questions only when they already have a
    // persisted answer. Missing demographics must still be collected.
    if (q.repeatable !== false) return true;

    if (
      q.freeTextFieldId &&
      String(visibilityData?.[q.id] ?? "") ===
        String(q.freeTextTriggerValue ?? "Other") &&
      !hasAnswer(visibilityData?.[q.freeTextFieldId])
    ) {
      return true;
    }

    return !hasAnswer(visibilityData?.[q.id]);
  });
};

/** Convert a flat list into `{ type:'single'|'group', … }` steps. */
export const buildSteps = (filteredQs) => {
  const steps = [];
  for (let i = 0; i < filteredQs.length; ) {
    const q = filteredQs[i];
    if (q.group) {
      const group = [q];
      let j = i + 1;
      while (j < filteredQs.length && filteredQs[j].group === q.group) {
        group.push(filteredQs[j++]);
      }
      steps.push({ type: "group", questions: group });
      i = j;
    } else {
      steps.push({ type: "single", question: q });
      i++;
    }
  }
  steps.push({ type: "review" });
  return steps;
};

/** Defaults for range sliders the user hasn’t touched yet. */
export const rangeDefaults = (questions, formData) =>
  questions.reduce((acc, q) => {
    if (
      q.fieldType === "range" &&
      (formData[q.id] == null || formData[q.id] === "")
    ) {
      acc[q.id] = q.min;
    }
    return acc;
  }, {});

/** Persist small localStorage blobs and navigate. */
export const persistAndGotoGuide = (filtered, data, nav) => {
  const autoFill = {};
  filtered.forEach((q) => {
    if (q.autoFillLocal) autoFill[q.id] = data[q.id];
  });
  localStorage.setItem("autoFillData", JSON.stringify(autoFill));
  localStorage.setItem("formMetadata", JSON.stringify(data));
  nav("/guide");
};
